import { useMemo, useState } from "react";
import "../shared/league.css";
import {
  buildCtx,
  Ctx,
  LEAGUE,
  Match,
  pIni,
  pName,
  Round,
  SIM,
  STATES,
  venueById,
  VENUES,
  visibleRounds,
} from "./simulate";

type Screen = "list" | "pre" | "joined";
type Tab = "info" | "prize" | "rule" | "reg" | "play" | "rank";

const STATE_OPTIONS: { value: string; label: string }[] = [
  { value: "waiting_start", label: "1 · Chờ BTC bắt đầu vòng 1" },
  { value: "has_match", label: "2 · Có trận hiện tại" },
  { value: "enter_result", label: "3 · Nhập kết quả" },
  { value: "waiting_confirm", label: "4 · Chờ đối thủ xác nhận" },
  { value: "match_done", label: "5 · Trận xong, chờ trận khác" },
  { value: "waiting_pairing", label: "6 · Chờ bốc cặp vòng sau" },
  { value: "bye", label: "7 · Miễn đấu" },
  { value: "eliminated", label: "8 · Bị loại" },
  { value: "finished", label: "9 · Giải kết thúc" },
];

const STATUS_META: Record<string, { cls: string; txt: string; dot?: boolean }> = {
  LIVE: { cls: "live", txt: "Đang đấu", dot: true },
  DRAFT: { cls: "wait", txt: "Chờ xác nhận" },
  CONFIRMED: { cls: "done", txt: "Đã xác nhận" },
  BYE: { cls: "red", txt: "Miễn đấu" },
};

const Balls = ({ lives }: { lives: number }) => (
  <div className="balls">
    {Array.from({ length: LEAGUE.lives }, (_, i) => (
      <div key={i} className={`ball ${i < lives ? "" : "gone"}`}>8</div>
    ))}
  </div>
);

const GroupChip = ({ l, dead }: { l: number; dead: boolean }) => {
  if (dead)
    return (
      <span className="grpcell" style={{ color: "var(--st-dead)", border: "1px solid var(--line)" }}>
        Đã loại
      </span>
    );
  const map: [string, string][] = [
    ["#B4CDBF", "rgba(110,139,124,.16)"],
    ["#E6D08F", "rgba(201,162,39,.14)"],
    ["#F0BDB4", "rgba(193,58,40,.16)"],
  ];
  const [c, b] = map[Math.min(l, 2)];
  return (
    <span className="grpcell" style={{ color: c, background: b }}>
      {l} thua
    </span>
  );
};

const You = () => <span className="you">BẠN</span>;

const MatchRow = ({ m }: { m: Match }) => {
  const st = STATUS_META[m.status] || STATUS_META.LIVE;
  const v = m.venue ? venueById(m.venue) : null;
  const mine = m.mine ? " mine" : "";
  if (m.bye) {
    return (
      <div className={`mrow${mine}`}>
        <div className="mtop">
          <span className="mtbl" style={{ color: "var(--red)" }}>MIỄN ĐẤU</span>
          <span className="mstat"><span className="chip red">Miễn đấu</span></span>
        </div>
        <div className="pline">
          <div className={`avas ${m.player === 0 ? "me" : ""}`}>{pIni(m.player!)}</div>
          <div className="pn">{pName(m.player!)}{m.player === 0 && <You />}</div>
          <div className="ps">+1</div>
        </div>
        <div className="byebox">Do số người còn lại là số lẻ. Được tính +1 thắng, không mất mạng và vào thẳng vòng tiếp theo.</div>
      </div>
    );
  }
  const done = m.status === "CONFIRMED" || m.status === "DRAFT";
  const PlayerLine = ({ id, score, isWin }: { id: number; score: number | null | undefined; isWin: boolean }) => (
    <div className={`pline ${done && isWin ? "win" : ""}`}>
      <div className={`avas ${id === 0 ? "me" : ""}`}>{pIni(id)}</div>
      <div className="pn">{pName(id)}{id === 0 && <You />}</div>
      <div className="ps">{score === null || score === undefined ? "—" : score}</div>
    </div>
  );
  return (
    <div className={`mrow${mine}`}>
      <div className="mtop">
        <span className="mtbl">BÀN {m.table}</span>
        <span className="mmeta">
          <span className="dot mut" />{v ? v.short : ""}<span className="dot mut" />Chạm {LEAGUE.race}
        </span>
        <span className="mstat">
          <span className={`chip ${st.cls}`}>{st.dot && <span className="pulse" />}{st.txt}</span>
        </span>
      </div>
      <PlayerLine id={m.winner!} score={done ? m.ws : null} isWin={true} />
      <PlayerLine id={m.loser!} score={done ? m.ls : null} isWin={false} />
      {m.status === "DRAFT" && (
        <div className="mfoot">
          <span className="tiny">Kết quả do {pName(m.winner!).split(" ").pop()} nhập • chờ đối phương xác nhận</span>
        </div>
      )}
    </div>
  );
};

const RoundGroup = ({
  r,
  idx,
  filter,
  openRounds,
  toggle,
}: {
  r: Round;
  idx: number;
  filter: string;
  openRounds: Record<string, boolean>;
  toggle: (k: string) => void;
}) => {
  let ms = r.matches;
  if (filter === "mine") ms = ms.filter((m) => m.mine);
  const key = "r" + r.round;
  const open = openRounds[key] !== undefined ? openRounds[key] : idx === 0;
  const live = (r as any).live;
  if (filter === "mine" && ms.length === 0) return null;
  const doneN = ms.filter((m) => m.status === "CONFIRMED" || m.status === "BYE").length;
  return (
    <div className={`roundgrp ${open ? "" : "closed"}`}>
      <div className="rh" onClick={() => toggle(key)}>
        <span className="rt">Vòng {r.round}</span>
        {live ? (
          <span className="chip live"><span className="pulse" />Đang diễn ra</span>
        ) : (
          <span className="chip done">Đã đóng</span>
        )}
        <span className="rc">{doneN}/{ms.length} trận {open ? "▾" : "▸"}</span>
      </div>
      <div className="rb">{ms.map((m, i) => <MatchRow key={i} m={m} />)}</div>
    </div>
  );
};

export default function SwissLeagueDemo() {
  const [screen, setScreen] = useState<Screen>("joined");
  const [state, setState] = useState("has_match");
  const [tab, setTab] = useState<Tab>("info");
  const [filter, setFilter] = useState<"all" | "mine">("mine");
  const [venue, setVenue] = useState<string | null>("v1");
  const [snapRound, setSnapRound] = useState<number | null>(null);
  const [openRounds, setOpenRounds] = useState<Record<string, boolean>>({});
  const [sheetScore, setSheetScore] = useState<{ me: number; opp: number }>({ me: 5, opp: 3 });
  const [referee, setReferee] = useState("r1");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const c = useMemo(() => buildCtx(state), [state]);

  const goScreen = (s: Screen) => {
    setScreen(s);
    if (s === "pre") setTab("prize");
    if (s === "joined") { setTab("info"); if (!venue) setVenue("v1"); }
    setSnapRound(null);
    window.scrollTo({ top: 0 });
  };
  const applyState = (s: string) => {
    setState(s);
    setOpenRounds({});
    setSnapRound(null);
    setSheetOpen(false);
    setModalOpen(false);
    if (STATES[s].sheet === "result") setSheetOpen(true);
  };
  const toggle = (k: string) =>
    setOpenRounds((o) => {
      const idx0 = k === "r" + (visibleRounds(state)[0]?.round);
      const cur = o[k] !== undefined ? o[k] : idx0;
      return { ...o, [k]: !cur };
    });

  /* ---------------- Status action per state ---------------- */
  const StatusAction = ({ c }: { c: Ctx }) => {
    const opp = c.cur && !c.cur.bye ? (c.cur.winner === 0 ? c.cur.loser! : c.cur.winner!) : null;
    const oppRow = opp !== null ? SIM.snaps[c.snapIdx].order.find((o) => o.id === opp) : null;
    const oppLives = oppRow ? LEAGUE.lives - oppRow.losses : LEAGUE.lives;
    const oppBlock = opp !== null && (
      <div className="opp">
        <div className="ava me">{pIni(0)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Bạn</div>
          <div className="tiny">{c.w}–{c.l} • {c.lives} mạng</div>
        </div>
        <div className="vsbadge">VS</div>
        <div style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{pName(opp)}</div>
          <div className="tiny">{oppRow ? `${oppRow.wins}–${oppRow.losses}` : "0–0"} • {oppLives} mạng</div>
        </div>
        <div className="ava">{pIni(opp)}</div>
      </div>
    );
    switch (state) {
      case "waiting_start":
        return (
          <>
            <div className="kicker">Chờ BTC bắt đầu vòng 1</div>
            <div className="expl">Bạn đã có suất tại <b style={{ color: "var(--chalk)" }}>{venueById(venue || "v1")?.name}</b>. BTC sẽ bốc cặp vòng 1 sau khi đóng check-in.</div>
            <div className="prog"><i style={{ width: "78%" }} /></div>
            <div className="tiny" style={{ marginTop: 6 }}>38/48 cơ thủ đã check-in • dự kiến bốc cặp 19:30 hôm nay</div>
            <div className="btns">
              <button className="btn btn-primary">Check-in tại địa điểm</button>
              <button className="btn btn-quiet" onClick={() => setTab("rule")}>Xem thể thức</button>
            </div>
          </>
        );
      case "has_match":
      case "enter_result":
        return (
          <>
            <div className="kicker">Bạn có trận ở vòng {c.curRound}</div>
            {oppBlock}
            <div className="btns">
              <button className="btn btn-primary" onClick={() => { setSheetScore({ me: 5, opp: 3 }); setSheetOpen(true); }}>Nhập kết quả</button>
              <button className="btn btn-ghost">Liên hệ đối thủ</button>
            </div>
          </>
        );
      case "waiting_confirm":
        return (
          <>
            <div className="kicker">Chờ đối thủ xác nhận</div>
            <div className="expl">Bạn đã gửi tỉ số <b style={{ color: "var(--red)", fontFamily: "var(--ff-d)", fontSize: 16 }}>{LEAGUE.race} – {c.cur ? c.cur.ls : 0}</b> (bạn thắng). Kết quả chỉ được tính sau khi {opp !== null ? pName(opp).split(" ").pop() : "đối thủ"} xác nhận.</div>
            {oppBlock}
            <div className="btns">
              <button className="btn btn-quiet">Nhắc đối thủ</button>
              <button className="btn btn-ghost" onClick={() => setSheetOpen(true)}>Sửa kết quả</button>
            </div>
          </>
        );
      case "match_done":
        return (
          <>
            <div className="kicker">Đã xác nhận • bạn thắng {LEAGUE.race}–{c.cur ? c.cur.ls : 3}</div>
            <div className="expl">Bạn giữ nguyên <b style={{ color: "var(--chalk)" }}>{c.lives} mạng</b> và ở lại nhóm {c.l} thua. Vòng {c.curRound} còn 5 trận chưa xong — BXH sẽ chốt khi vòng đóng.</div>
            <div className="prog"><i style={{ width: "70%" }} /></div>
            <div className="tiny" style={{ marginTop: 6 }}>16/23 trận vòng {c.curRound} đã có kết quả</div>
            <div className="btns"><button className="btn btn-ghost" onClick={() => setTab("rank")}>Xem BXH tạm tính</button></div>
          </>
        );
      case "waiting_pairing":
        return (
          <>
            <div className="kicker">Chờ bốc cặp vòng {c.cfg.roundsDone + 1}</div>
            <div className="expl">Vòng {c.cfg.roundsDone} đã đóng. Bạn đang ở nhóm {c.l} thua với {c.lives} mạng — vòng sau bạn sẽ gặp người cùng nhóm.</div>
            <div className="btns"><button className="btn btn-ghost" onClick={() => setTab("rank")}>Xem BXH sau vòng {c.cfg.roundsDone}</button></div>
          </>
        );
      case "bye":
        return (
          <>
            <div className="kicker" style={{ color: "var(--red)" }}>Bạn được miễn đấu vòng {c.curRound}</div>
            <div className="expl">Do số người còn lại là số lẻ. Bạn được tính <b style={{ color: "var(--chalk)" }}>+1 thắng</b>, <b style={{ color: "var(--chalk)" }}>không mất mạng</b> và vào thẳng vòng tiếp theo.</div>
            <div className="btns"><button className="btn btn-quiet" onClick={() => setTab("rank")}>Xem BXH</button></div>
          </>
        );
      case "eliminated":
        return (
          <>
            <div className="kicker" style={{ color: "var(--steel)" }}>Bạn đã dừng bước ở vòng {c.cfg.roundsDone}</div>
            <div className="expl">Bạn thua đủ {LEAGUE.lives} trận nên hết mạng. Thành tích cuối: <b style={{ color: "var(--chalk)" }}>{c.w} thắng – {c.l} thua</b>. {c.w} trận đã xác minh được tính vào rating Biadi.</div>
            <div className="btns">
              <button className="btn btn-ghost" onClick={() => setTab("rank")}>Xem BXH</button>
              <button className="btn btn-quiet" onClick={() => { setFilter("mine"); setTab("play"); }}>Xem lại trận của tôi</button>
            </div>
          </>
        );
      case "finished":
        return (
          <>
            <div className="kicker">Giải đã kết thúc</div>
            <div className="expl">Bạn xếp <b style={{ color: "var(--red)", fontFamily: "var(--ff-d)", fontSize: 17 }}>hạng {c.rank}</b>/{LEAGUE.players} với {c.w} thắng – {c.l} thua. Vô địch: <b style={{ color: "var(--chalk)" }}>{pName(SIM.snaps[SIM.snaps.length - 1].order[0].id)}</b>.</div>
            <div className="btns">
              <button className="btn btn-primary" onClick={() => setTab("rank")}>Xem BXH chung cuộc</button>
              <button className="btn btn-quiet" onClick={() => setTab("prize")}>Xem giải thưởng</button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const StatusCard = () => {
    const chipMap: Record<string, [string, JSX.Element | string]> = {
      waiting_start: ["chip", "Chờ vòng 1"],
      has_match: ["chip live", <><span className="pulse" />Có trận</>],
      enter_result: ["chip live", <><span className="pulse" />Có trận</>],
      waiting_confirm: ["chip wait", "Chờ xác nhận"],
      match_done: ["chip done", "Đã xong vòng"],
      waiting_pairing: ["chip wait", "Chờ bốc cặp"],
      bye: ["chip red", "Miễn đấu"],
      eliminated: ["chip dead", "Bị loại"],
      finished: ["chip solid", "Kết thúc"],
    };
    const [cc, ct] = chipMap[state];
    const roundTxt = c.cfg.current
      ? `Vòng ${c.cfg.current}`
      : state === "waiting_start"
      ? "Trước vòng 1"
      : c.over
      ? `Đã xong ${c.cfg.roundsDone} vòng`
      : `Sau vòng ${c.cfg.roundsDone}`;
    return (
      <div className="status">
        <div className="sh">
          <span className="rnd">{roundTxt}<span className="dot" /><span style={{ color: "var(--steel)", fontWeight: 600, fontSize: 11.5 }}>/{LEAGUE.est} vòng</span></span>
          <span className={cc}>{ct}</span>
        </div>
        <div className="stats4">
          <div className="st"><div className="k">T–B</div><div className="v"><span style={{ color: "var(--red)" }}>{c.w}</span>–{c.l}</div></div>
          <div className="st"><div className="k">Mạng còn</div><Balls lives={c.lives} /></div>
          <div className="st"><div className="k">Nhóm</div><div className="v sm"><GroupChip l={c.l} dead={c.dead} /></div></div>
          <div className="st"><div className="k">Hạng</div><div className="v">{c.dead && !c.over ? "—" : c.rank}</div></div>
        </div>
        <div className="action"><StatusAction c={c} /></div>
        <div className="rankline">
          <span className="rl">{c.over ? "Giải đấu đã kết thúc" : `Còn ${c.aliveCount}/${LEAGUE.players} cơ thủ • thể thức Swiss ${LEAGUE.lives} mạng`}</span>
          <a href="#" onClick={(e) => { e.preventDefault(); setTab("rank"); }}>BXH ›</a>
        </div>
      </div>
    );
  };

  /* ---------------- Tabs ---------------- */
  const TabPlay = () => {
    const rs = visibleRounds(state);
    const groups = rs.map((r, i) => <RoundGroup key={r.round} r={r} idx={i} filter={filter} openRounds={openRounds} toggle={toggle} />).filter(Boolean);
    const anyVisible = groups.some((g) => g !== null);
    return (
      <div className="playgrid">
        <div className="col-left"><StatusCard /></div>
        <div className="col-right">
          <div className="mfilter">
            <div className="seg">
              <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")}>Tất cả</button>
              <button className={filter === "mine" ? "on" : ""} onClick={() => setFilter("mine")}>Của bạn</button>
            </div>
          </div>
          {rs.length === 0 ? (
            <div className="emptybox">Chưa có vòng nào. Danh sách trận sẽ hiện ngay khi BTC bốc cặp vòng 1.</div>
          ) : !anyVisible ? (
            <div className="emptybox">Bạn chưa có trận nào ở bộ lọc này.</div>
          ) : (
            groups
          )}
        </div>
      </div>
    );
  };

  const TabRank = () => {
    const maxSnap = c.cfg.roundsDone;
    const sel = snapRound === null ? maxSnap : Math.min(snapRound, maxSnap);
    const snap = SIM.snaps[sel];
    const myIdx = snap.order.findIndex((o) => o.id === 0);
    const my = snap.order[myIdx];
    return (
      <>
        <div className="sec-h"><span className="label">BXH theo vòng</span></div>
        <div className="roundchips">
          {Array.from({ length: maxSnap + 1 }, (_, i) => (
            <button key={i} className={`rchip ${i === sel ? "on" : ""}`} onClick={() => setSnapRound(i)}>
              {i === 0 ? "Trước Vòng 1" : "Vòng " + i}
            </button>
          ))}
        </div>
        <div className="snapnote">
          {sel === 0
            ? "Bảng khởi điểm — xếp theo rating Biadi trước khi bốc cặp vòng 1."
            : `Kết quả sau khi vòng ${sel} đóng • cập nhật 20:${sel < 5 ? "1" : "4"}5 ngày ${11 + sel}/09/2026.`}
        </div>
        <div className="sec" style={{ padding: "6px 10px 10px" }}>
          <table className="tbl">
            <thead>
              <tr>
                {/* 44 chứ không phải 38: từ khi bảng dùng table-layout fixed, con số này là
                    ràng buộc thật, mà chữ "HẠNG" ở cỡ 10px kèm giãn chữ 1.1px đo được cần
                    43px - để 38 là tiêu đề đè sang cột bên cạnh. */}
                <th style={{ width: 44 }}>Hạng</th>
                <th className="l">Cơ thủ</th>
                <th style={{ width: 56 }}>T–B</th>
                <th style={{ width: 74 }}>Nhóm</th>
                <th style={{ width: 58 }}>Mạng</th>
              </tr>
            </thead>
            <tbody>
              {snap.order.map((o, i) => {
                const dead = o.elimRound !== null;
                const me = o.id === 0;
                return (
                  <tr key={o.id} className={`${me ? "me" : ""} ${dead ? "dead" : ""}`}>
                    <td><span className="rkcell">{i + 1}</span></td>
                    <td className="l">
                      <div className="namecell">
                        <div className={`avas ${me ? "me" : ""}`} style={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 700, background: me ? "linear-gradient(135deg,var(--brick),#5C1710)" : "linear-gradient(135deg,#28324A,#3A2019)" }}>{pIni(o.id)}</div>
                        <span className="nm">{pName(o.id)}</span>{me && <You />}
                      </div>
                    </td>
                    <td><span className="wl"><b>{o.wins}</b>–{o.losses}</span></td>
                    <td>{dead ? <span className="tiny">Vòng {o.elimRound}</span> : <GroupChip l={o.losses} dead={false} />}</td>
                    <td>
                      {dead ? (
                        <span className="tiny">—</span>
                      ) : (
                        <div className="balls" style={{ justifyContent: "center" }}>
                          {Array.from({ length: LEAGUE.lives }, (_, k) => (
                            <div key={k} className={`ball ${k < LEAGUE.lives - o.losses ? "" : "gone"}`} style={{ width: 11, height: 11, fontSize: 0 }}>8</div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="tiny" style={{ padding: "10px 2px" }}>
          Tiêu chí xếp: số trận thua ↑ <span className="dot mut" /> số trận thắng ↓ <span className="dot mut" /> điểm đối thủ (SoS) ↓ <span className="dot mut" /> rating Biadi ↓
        </div>
        <div className="myrankbar">
          <span className="rkcell" style={{ color: "var(--red)" }}>#{myIdx + 1}</span>
          <div className="ava me" style={{ width: 28, height: 28, fontSize: 11 }}>{pIni(0)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{pName(0)}</div>
            <div className="tiny">{my.wins} thắng – {my.losses} thua {my.elimRound ? `• bị loại Vòng ${my.elimRound}` : `• ${LEAGUE.lives - my.losses} mạng`}</div>
          </div>
          <span className="chip red">Bạn</span>
        </div>
      </>
    );
  };

  const TabPrize = () => {
    const rows = [
      ["Vô địch", "15.000.000", "Cúp + áo Biadi"],
      ["Á quân", "8.000.000", "Cơ Predator"],
      ["Hạng 3", "4.000.000", "Găng + phấn"],
      ["Hạng 4", "2.000.000", "—"],
      ["Hạng 5–8", "1.000.000", "—"],
      ["Hạng 9–16", "500.000", "—"],
    ];
    return (
      <div className="sec">
        <div className="prizehead"><span>Thứ hạng</span><span style={{ textAlign: "right" }}>Tiền mặt</span><span>Hiện vật</span></div>
        {rows.map((r, i) => (
          <div key={i} className="prizerow"><span className="p1">{r[0]}</span><span className="p2">{r[1]} đ</span><span className="small">{r[2]}</span></div>
        ))}
        <div className="tiny" style={{ marginTop: 12 }}>Tổng giải thưởng 38.000.000 đ <span className="dot" /> trao ngay sau trận chung kết.</div>
      </div>
    );
  };

  const TabRule = () => (
    <div className="sec rulebody">
      <div className="metarow" style={{ marginBottom: 10 }}>
        <span className="chip solid">Swiss</span><span className="dot" /><span>{LEAGUE.lives} mạng</span>
        <span className="dot" /><span>Chạm {LEAGUE.race}</span><span className="dot" /><span>dự kiến {LEAGUE.est} vòng</span>
      </div>
      <h4>Cách tính mạng</h4>
      <ul>
        <li>Mỗi cơ thủ có <b>{LEAGUE.lives} mạng</b>. Mỗi trận thua mất 1 mạng, hết mạng là dừng bước.</li>
        <li>Ai cũng được chơi tối thiểu {LEAGUE.lives} trận — không bị loại ngay ở vòng 1.</li>
      </ul>
      <h4>Bốc cặp mỗi vòng</h4>
      <ul>
        <li>Người cùng <b>số trận thua</b> được xếp gặp nhau. Thắng thì giữ nhóm, thua thì tụt xuống nhóm dưới.</li>
        <li>Hệ thống tránh cho hai người đã gặp nhau đấu lại.</li>
        <li>Khi số người còn lại là <b>số lẻ</b>, một người được <b>miễn đấu</b>: tính +1 thắng và không mất mạng.</li>
      </ul>
      <h4>Xếp hạng</h4>
      <ul>
        <li>Ít thua hơn xếp trên. Bằng nhau thì xét nhiều thắng hơn, rồi tới điểm đối thủ (SoS), rồi rating Biadi.</li>
        <li>BXH chốt sau khi mỗi vòng đóng — xem theo từng vòng ở tab BXH.</li>
      </ul>
      <h4>Lệ phí & hoàn cọc</h4>
      <ul><li>Lệ phí tham gia <b>{LEAGUE.fee} đ</b>, đóng tại địa điểm khi check-in.</li></ul>
    </div>
  );

  const TabRegistered = () => {
    const listP = SIM.P.slice(0, 18);
    return (
      <div className="sec" style={{ padding: "6px 12px 12px" }}>
        <div className="prizehead" style={{ gridTemplateColumns: "1fr 70px 60px" }}>
          <span>Cơ thủ</span><span style={{ textAlign: "center" }}>Địa điểm</span><span style={{ textAlign: "center" }}>Hạng</span>
        </div>
        {listP.map((p, i) => (
          <div key={p.id} className="prizerow" style={{ gridTemplateColumns: "1fr 70px 60px", alignItems: "center" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span className={`avas ${i === 0 ? "me" : ""}`} style={{ width: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 700, background: i === 0 ? "linear-gradient(135deg,var(--brick),#5C1710)" : "linear-gradient(135deg,#28324A,#3A2019)" }}>{p.ini}</span>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: 13.5 }}>{p.name}</span>
              {i === 0 && <You />}
            </span>
            <span className="tiny" style={{ textAlign: "center" }}>{VENUES[i % 3].short}</span>
            <span className="tiny" style={{ textAlign: "center" }}>{["A", "B", "B+", "C"][i % 4]}</span>
          </div>
        ))}
        <div className="tiny" style={{ marginTop: 12, textAlign: "center" }}>Hiển thị 18/48 cơ thủ • <a href="#" onClick={(e) => e.preventDefault()}>xem tất cả</a></div>
      </div>
    );
  };

  const TabInfo = () => {
    const v = venueById(venue || "v1")!;
    const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v.name} ${v.addr}`)}`;
    return (
      <>
        <div className="sec">
          <div className="infoline"><div className="ic">📅</div><div><div className="k">Đăng ký</div><div className="v acc">{LEAGUE.reg}</div></div></div>
          <div className="divider" />
          <div className="infoline"><div className="ic">🎱</div><div><div className="k">Thi đấu</div><div className="v">{LEAGUE.play}</div></div></div>
          <div className="divider" />
          <div className="infoline"><div className="ic">📍</div><div style={{ flex: 1, minWidth: 0 }}>
            <div className="k">Địa điểm của bạn</div>
            <a className="infolink" href={maps} target="_blank" rel="noreferrer">{v.name}</a>
            <div className="small">{v.addr}</div>
            <div style={{ marginTop: 8 }}><span className="chip red">Đã cố định khi đăng ký</span></div>
          </div></div>
        </div>
        <div className="jhead">Giải thưởng</div>
        <TabPrize />
        <div className="jhead">Thể lệ thi đấu</div>
        <TabRule />
      </>
    );
  };

  /* ---------------- Header / cover ---------------- */
  const Cover = () => (
    <div className="cover">
      <span className="fmt chip solid">Swiss</span>
      <div className="cvin"><div className="big">Biadi Open<br />Swiss #4</div><div className="sub">Rank • Compete • Trust</div></div>
    </div>
  );

  const HeaderInfo = ({ joined }: { joined: boolean }) => {
    const v = joined ? venueById(venue || "v1") : null;
    return (
      <>
        <Cover />
        <h1 className="h1" style={{ marginTop: 14 }}>{LEAGUE.name}</h1>
        <div className="metarow" style={{ marginTop: 8 }}>
          <span>{LEAGUE.format}</span><span className="dot" /><span>{LEAGUE.lives} mạng</span>
          <span className="dot" /><span>Chạm {LEAGUE.race}</span><span className="dot" />
          <span>{joined ? "1 địa điểm của bạn" : VENUES.length + " địa điểm"}</span>
          <span className="dot" /><span>{LEAGUE.players} cơ thủ</span>
        </div>
        <div className="sec">
          <div className="infoline"><div className="ic">📅</div><div><div className="k">Đăng ký</div><div className="v acc">{LEAGUE.reg}</div></div></div>
          <div className="divider" />
          <div className="infoline"><div className="ic">🎱</div><div><div className="k">Thi đấu</div><div className="v">{LEAGUE.play}</div></div></div>
          <div className="divider" />
          {joined ? (
            <div className="infoline"><div className="ic">📍</div><div style={{ flex: 1, minWidth: 0 }}>
              <div className="k">Địa điểm của bạn</div>
              <div className="v">{v!.name}</div>
              <div className="small">{v!.addr}</div>
              <div style={{ marginTop: 8 }}><span className="chip red">Đã cố định khi đăng ký</span></div>
            </div></div>
          ) : (
            <div className="infoline"><div className="ic">📍</div><div style={{ flex: 1, minWidth: 0 }}>
              <div className="k">Địa điểm</div>
              <div className="v">{VENUES.length} địa điểm — chọn 1 nơi bạn sẽ thi đấu</div>
              <div className="small">{VENUES.map((x) => x.short).join(" · ")}</div>
            </div></div>
          )}
          {joined && (
            <div style={{ marginTop: 12 }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v!.name} ${v!.addr}`)}`}
                target="_blank"
                rel="noreferrer"
              >
                📍 Chỉ đường tới {v!.name} ›
              </a>
            </div>
          )}
        </div>
      </>
    );
  };

  /* ---------------- Screens ---------------- */
  const Navbar = ({ back, brand = true }: { back?: () => void; brand?: boolean }) => (
    <div className="navbar">
      <button className="back" onClick={back}>‹</button>
      <span className="ttl">{screen === "list" ? "Giải đấu" : "Thông tin giải đấu"}</span>
      {brand && <span className="brandmark"><span className="bmk">B</span>BIADI</span>}
    </div>
  );

  const ScreenList = () => {
    const leagues = [
      { n: LEAGUE.name, fmt: "Swiss", lives: 3, venues: 3, d: "12/09 – 20/09/2026", open: true, reg: "10/09", ini: "B4" },
      { n: "GIẢI VÒNG TRÒN THÁNG 9", fmt: "Vòng tròn", lives: null as number | null, venues: 1, d: "05/09 – 28/09/2026", open: true, reg: "04/09", ini: "VT" },
      { n: "BIADI KNOCKOUT CUP", fmt: "Loại trực tiếp", lives: null as number | null, venues: 2, d: "22/09 – 24/09/2026", open: false, reg: "18/09", ini: "KO" },
    ];
    return (
      <>
        <Navbar />
        <div className="pad">
          <div className="sec-h"><span className="label">Đang mở đăng ký</span><span className="tiny">3 giải</span></div>
          {leagues.map((L, i) => (
            <div key={i} className="lcard" onClick={i === 0 ? () => goScreen("pre") : undefined}>
              <div className="lthumb"><span>{L.ini}</span></div>
              <div className="lbody">
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="lname">{L.n}</div>
                    <div className="metarow" style={{ marginTop: 6, fontSize: 12 }}>
                      <span style={{ color: "var(--red)", fontWeight: 700 }}>{L.fmt}</span>
                      {L.lives && <><span className="dot" /><span>{L.lives} mạng</span></>}
                      <span className="dot" /><span>{L.venues > 1 ? L.venues + " địa điểm" : "Biadi Arena Q.1"}</span>
                    </div>
                    <div className="small" style={{ marginTop: 5 }}>Thi đấu: {L.d}</div>
                  </div>
                  <span className="chevron">›</span>
                </div>
                {L.open ? (
                  <button className="btn btn-primary btn-block btn-sm" style={{ marginTop: 10 }} onClick={i === 0 ? () => goScreen("pre") : undefined}>Đăng ký (hạn {L.reg})</button>
                ) : (
                  <div className="small" style={{ marginTop: 10, color: "var(--st-dead)" }}>Chưa tới thời gian đăng ký</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const ScreenPre = () => {
    const chosen = venue ? venueById(venue) : null;
    return (
      <>
        <Navbar back={() => goScreen("list")} />
        <div className="pad">
          <HeaderInfo joined={false} />
          <div className="sec-h"><span className="label">Chọn địa điểm thi đấu</span><span className="tiny">Bắt buộc</span></div>
          <div>
            {VENUES.map((v) => {
              const full = v.taken >= v.cap;
              const on = venue === v.id;
              return (
                <div key={v.id} className={`venue ${on ? "on" : ""} ${full ? "off" : ""}`} onClick={full ? undefined : () => setVenue(v.id)}>
                  <div className="radio" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="vname">{v.name}</div>
                    <div className="vaddr">{v.addr}</div>
                    <div className="vmeta">
                      <span className="chip">{v.tables} bàn</span>
                      <span className="chip">{v.dist}</span>
                      <span className={`chip ${full ? "dead" : v.cap - v.taken <= 4 ? "wait" : ""}`}>{full ? "Hết suất" : `Còn ${v.cap - v.taken}/${v.cap} suất`}</span>
                    </div>
                    <div className={`slots ${full ? "full" : ""}`}><i style={{ width: `${Math.round((v.taken / v.cap) * 100)}%` }} /></div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="tiny" style={{ marginTop: 2 }}>Bạn sẽ thi đấu toàn bộ các vòng tại địa điểm đã chọn. Không đổi được sau khi đăng ký.</div>

          <div className="tabs">
            <button className={tab === "prize" ? "on" : ""} onClick={() => setTab("prize")}>Giải thưởng</button>
            <button className={tab === "rule" ? "on" : ""} onClick={() => setTab("rule")}>Thể thức</button>
            <button className={tab === "reg" ? "on" : ""} onClick={() => setTab("reg")}>Đã đăng ký</button>
          </div>
          <div className="tabpane">
            {tab === "prize" && <TabPrize />}
            {tab === "rule" && <TabRule />}
            {tab === "reg" && <TabRegistered />}
            {tab !== "prize" && tab !== "rule" && tab !== "reg" && <TabPrize />}
          </div>
        </div>
        <div className="actionbar"><div className="inner">
          {chosen ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span className="chip red">📍 {chosen.name}</span>
              <span className="tiny" style={{ marginLeft: "auto" }}>Lệ phí {LEAGUE.fee} đ</span>
            </div>
          ) : (
            <div className="tiny" style={{ marginBottom: 10 }}>Chọn 1 địa điểm ở trên để tiếp tục</div>
          )}
          <button className="btn btn-primary btn-block" disabled={!chosen} onClick={() => chosen && setModalOpen(true)}>Tham gia ngay</button>
        </div></div>
      </>
    );
  };

  const ScreenJoined = () => {
    const active = tab === "play" || tab === "rank" ? tab : "info";
    return (
      <>
        <Navbar back={() => goScreen("list")} brand={false} />
        <div className="pad">
          <div className="jbanner"><span className="fmt chip solid">Swiss</span></div>
          <h1 className="jname">{LEAGUE.name}</h1>
          <div className="tabs">
            <button className={active === "info" ? "on" : ""} onClick={() => setTab("info")}>Thông tin chung</button>
            <button className={active === "play" ? "on" : ""} onClick={() => setTab("play")}>Thi đấu</button>
            <button className={active === "rank" ? "on" : ""} onClick={() => setTab("rank")}>BXH</button>
          </div>
          <div className="tabpane">
            {active === "info" && <TabInfo />}
            {active === "play" && <TabPlay />}
            {active === "rank" && <TabRank />}
          </div>
        </div>
      </>
    );
  };

  /* ---------------- Sheet / modal ---------------- */
  const ResultSheet = () => {
    if (!c.cur || c.cur.bye) return null;
    const opp = c.cur.winner === 0 ? c.cur.loser! : c.cur.winner!;
    const s = sheetScore;
    const valid = s.me === LEAGUE.race || s.opp === LEAGUE.race;
    const step = (who: "me" | "opp", delta: number) =>
      setSheetScore((sc) => ({ ...sc, [who]: Math.max(0, Math.min(LEAGUE.race, sc[who] + delta)) }));
    return (
      <div className="sheetwrap">
        <div className="sheetbd" onClick={() => setSheetOpen(false)} />
        <div className="sheet">
          <div className="grab" />
          <div className="sc">
            <div className="sttl">Nhập kết quả</div>
            <div className="small">Vòng {c.curRound} • Bàn {c.cur.table} • {venueById(c.cur.venue)?.name} • Chạm {LEAGUE.race}</div>
            <div className="scoreinput">
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <div className="ava me">{pIni(0)}</div><div style={{ fontWeight: 600, fontSize: 14 }}>Bạn</div>
              </div>
              <div className="stepper"><button onClick={() => step("me", -1)}>–</button><span className="sv">{s.me}</span><button onClick={() => step("me", 1)}>+</button></div>
            </div>
            <div className="scoreinput">
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <div className="ava">{pIni(opp)}</div>
                <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pName(opp)}</div>
              </div>
              <div className="stepper"><button onClick={() => step("opp", -1)}>–</button><span className="sv">{s.opp}</span><button onClick={() => step("opp", 1)}>+</button></div>
            </div>
            <div className="tiny" style={{ marginTop: 8 }}>
              {valid ? (
                <>Người thắng: <b style={{ color: "var(--red)" }}>{s.me > s.opp ? "Bạn" : pName(opp)}</b> • người thua mất 1 mạng.</>
              ) : (
                <span style={{ color: "var(--st-wait)" }}>Một bên phải đạt {LEAGUE.race} ván để kết thúc trận.</span>
              )}
            </div>
            <div className="fieldlbl">Trọng tài xác nhận</div>
            <div className="selrow">
              {["Nguyễn Văn Tú", "Trần Hữu Phước", "Không có trọng tài"].map((r, i) => (
                <span key={i} className={`pill ${referee === "r" + i ? "on" : ""}`} onClick={() => setReferee("r" + i)}>{r}</span>
              ))}
            </div>
            <div className="fieldlbl">Ảnh bảng điểm (tuỳ chọn)</div>
            <div className="uploadbox">📷 Chụp hoặc chọn ảnh</div>
            <div className="callout" style={{ marginTop: 14 }}>
              <div className="tiny" style={{ color: "#F0BDB4" }}>Kết quả chỉ được tính sau khi đối thủ xác nhận. Sau khi cả hai xác nhận, trận được tính vào rating Biadi và không sửa được.</div>
            </div>
            <button className="btn btn-primary btn-block" style={{ marginTop: 14 }} disabled={!valid} onClick={() => { setSheetOpen(false); applyState("waiting_confirm"); }}>Gửi kết quả</button>
            <button className="btn btn-quiet btn-block" style={{ marginTop: 8 }} onClick={() => setSheetOpen(false)}>Huỷ</button>
          </div>
        </div>
      </div>
    );
  };

  const ConfirmJoinModal = () => {
    const v = venueById(venue);
    if (!v) return null;
    return (
      <div className="modalwrap">
        <div className="modalbd" onClick={() => setModalOpen(false)} />
        <div className="modal">
          <div className="label" style={{ marginBottom: 10 }}>Xác nhận tham gia</div>
          <div className="callout">
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{LEAGUE.name}</div>
            <div className="small" style={{ color: "#E9CFC9" }}>Bạn đăng ký thi đấu tại <b style={{ color: "#fff" }}>{v.name}</b> — {v.addr}.</div>
            <div className="small" style={{ marginTop: 8, color: "#E9CFC9" }}>Địa điểm <b style={{ color: "#fff" }}>không đổi được</b> sau khi đăng ký. Lệ phí {LEAGUE.fee} đ đóng tại chỗ khi check-in.</div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { setModalOpen(false); setState("waiting_start"); goScreen("joined"); }}>Xác nhận</button>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setModalOpen(false)}>Để sau</button>
          </div>
        </div>
      </div>
    );
  };

  // Demo mode: prototype control bar + manual state switching.
  // On (dev/staging via REACT_APP_SWISS_DEMO=true) → you can walk all 9 states.
  // Off (production, flag absent) → the control bar is hidden.
  const demo = process.env.REACT_APP_SWISS_DEMO === "true";

  return (
    <div className="biadi">
      {demo && (
        <div className="proto">
          <span className="pl">Màn</span>
          <div className="seg">
            <button className={screen === "list" ? "on" : ""} onClick={() => goScreen("list")}>Danh sách giải</button>
            <button className={screen === "pre" ? "on" : ""} onClick={() => goScreen("pre")}>Chi tiết · chưa ĐK</button>
            <button className={screen === "joined" ? "on" : ""} onClick={() => goScreen("joined")}>Chi tiết · đã ĐK</button>
          </div>
          <span className="pl" style={{ marginLeft: 6 }}>State Swiss</span>
          <select value={state} disabled={screen !== "joined"} style={{ opacity: screen !== "joined" ? 0.4 : 1 }} onChange={(e) => applyState(e.target.value)}>
            {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}

      <div className="shell">
        {screen === "list" && <ScreenList />}
        {screen === "pre" && <ScreenPre />}
        {screen === "joined" && <ScreenJoined />}
      </div>

      {sheetOpen && <ResultSheet />}
      {modalOpen && <ConfirmJoinModal />}
    </div>
  );
}
